'use client';

import { useSignInOnClick } from 'src/hooks/useSignInOnClick';
import routes from 'src/routes';

import { useTranslations } from 'next-intl';
import React from 'react';
import { Alert, Button, ProcessList, ProcessListHeading, ProcessListItem } from '@trussworks/react-uswds';

import CustomLink from 'src/components/CustomLink';
import Icon from 'src/components/Icon';

export function View() {
  const t = useTranslations('login');
  const signInOnClick = useSignInOnClick();

  return (
    <>
      <h1>{t('portal_intro.title')}</h1>
      <p className="usa-intro text-bold">{t('portal_intro.body')}</p>

      <Button type="button" outline onClick={signInOnClick} style={{ backgroundColor: '#E8EEF4' }}>
        {t('portal_intro.login_link')} <Icon className="margin-left-05" name="arrow_forward" />
      </Button>

      <Alert type="info" className="margin-top-4" headingLevel="h6" slim>
        <strong>{t('portal_intro.alert_title')}</strong> {t('portal_intro.alert_body')}
      </Alert>

      <div className="ma__custom-divider">
        <p>{t('divider_text')}</p>
      </div>

      <section>
        <h2 className="ma__comp-heading">{t('about_ccfa.title')}</h2>
        <p>
          {t.rich('about_ccfa.body', {
            learn_more_link: (content) => (
              <CustomLink external href={routes.external.massgov.howToApplyForChildCare}>
                {content}
              </CustomLink>
            ),
          })}
        </p>
      </section>

      <section>
        <h2 className="ma__comp-heading">{t('how_to_apply.title')}</h2>

        {t.rich('how_to_apply.body', {
          strong: (content) => <strong>{content}</strong>,
          p: (content) => <p>{content}</p>,
        })}

        <Button type="button" outline onClick={signInOnClick} style={{ backgroundColor: '#E8EEF4' }}>
          {t('how_to_apply.link')} <Icon className="margin-left-05" name="arrow_forward" />
        </Button>
      </section>

      <section className="login-page">
        <h2 className="ma__comp-heading">{t('applying_for_benefits.title')}</h2>
        <p>
          {t.rich('applying_for_benefits.body', {
            telephone_link: () => (
              <CustomLink href={`tel:${routes.external.phones.mass_211_contact_number}`}>
                {routes.external.phones.mass_211_contact_number}
              </CustomLink>
            ),
          })}
        </p>
        <ProcessList>
          <ProcessListItem>
            <ProcessListHeading type="h3">{t('applying_for_benefits.step_1_title')}</ProcessListHeading>
            <p>{t('applying_for_benefits.step_1_body')}</p>
          </ProcessListItem>
          <ProcessListItem>
            <ProcessListHeading type="h3">{t('applying_for_benefits.step_2_title')}</ProcessListHeading>
            <p>
              {t.rich('applying_for_benefits.step_2_body', {
                documents_link: (content) => (
                  <CustomLink href={routes.external.massgov.documents}>{content}</CustomLink>
                ),
                eligibility_link: (content) => (
                  <CustomLink href={routes.external.massgov.howToApplyForChildCare}>{content}</CustomLink>
                ),
              })}
            </p>
          </ProcessListItem>
          <ProcessListItem>
            <ProcessListHeading type="h3">{t('applying_for_benefits.step_3_title')}</ProcessListHeading>
            <p>
              {t.rich('applying_for_benefits.step_3_body', {
                process_link: (content) => <CustomLink href={routes.internal.timeline}>{content}</CustomLink>,
              })}
            </p>
          </ProcessListItem>
          <ProcessListItem>
            <ProcessListHeading type="h3">{t('applying_for_benefits.step_4_title')}</ProcessListHeading>
            <p>{t('applying_for_benefits.step_4_body')}</p>
          </ProcessListItem>
          <ProcessListItem>
            <ProcessListHeading type="h3">{t('applying_for_benefits.step_5_title')}</ProcessListHeading>
            <p>{t('applying_for_benefits.step_5_body')}</p>
          </ProcessListItem>
          <ProcessListItem>
            <ProcessListHeading type="h3">{t('applying_for_benefits.step_6_title')}</ProcessListHeading>
            <p>{t('applying_for_benefits.step_6_body')}</p>
          </ProcessListItem>
        </ProcessList>

        <CustomLink external href={routes.external.massgov.howToApplyForChildCare}>
          {t('applying_for_benefits.learn_more_link')}
        </CustomLink>
      </section>

      <section>
        <h2 className="ma__comp-heading">{t('admin.title')}</h2>
        <p>{t('admin.body')}</p>
        <p>
          <CustomLink external href={routes.external.massgov.managingChildCareAndFinancialAssistance}>
            {t('admin.learn_more_link')}
          </CustomLink>
        </p>
        <p>
          <CustomLink
            button
            style={{ backgroundColor: '#E8EEF4' }}
            variation="outline"
            href={process.env.NEXT_PUBLIC_LOGIN_ADMIN_URL ?? '/admin/'}
          >
            {t('admin.login_link')} <Icon className="margin-left-05" name="arrow_forward" />
          </CustomLink>
        </p>
      </section>
    </>
  );
}
